import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, Download, Package, MessageSquare, FolderOpen, CreditCard, Upload, Trash2, Image as ImageIcon, Activity } from "lucide-react";
import { adminFetch, adminSetOrderStatus, adminDeleteOrder, adminConfirmCash, adminUploadProof, adminUploadProductImage, adminDeleteProductImage, fetchProducts, money, API } from "@/lib/api";
import { PRODUCT_IMAGES, CATEGORY_IMAGES } from "@/data/catalog";
import { useProductImages, productImageUrl } from "@/context/ProductImagesContext";
import AdminWiPay from "@/components/AdminWiPay";
import AdminTracking from "@/components/AdminTracking";

const STORAGE_KEY = "tlf_admin_key";

const SECTIONS = [
  { id: "orders", label: "Orders", icon: Package },
  { id: "inquiries", label: "Bulk Inquiries", icon: MessageSquare },
  { id: "files", label: "Customer Artwork", icon: FolderOpen },
  { id: "images", label: "Product Images", icon: ImageIcon },
  { id: "tracking", label: "Tracking", icon: Activity },
  { id: "settings", label: "Payment Settings", icon: CreditCard },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-JM", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const STATUS_STAGES = [
  { id: "received", label: "Order Received", cls: "border-brand-cyan/60 text-brand-cyan" },
  { id: "production", label: "In Production", cls: "border-brand-magenta/60 text-brand-magenta" },
  { id: "ready", label: "Ready for Pickup / Shipped", cls: "border-brand-yellow/60 text-brand-yellow" },
  { id: "completed", label: "Completed", cls: "border-emerald-400/60 text-emerald-400" },
];

const PAYMENT_META = {
  paid: { label: "Paid in Full", cls: "border-emerald-400/60 text-emerald-400" },
  deposit_paid: { label: "Deposit Paid", cls: "border-brand-yellow/60 text-brand-yellow" },
  awaiting_payment: { label: "Awaiting Cash Payment", cls: "border-brand-magenta/60 text-brand-magenta" },
  pending: { label: "Online Payment Pending", cls: "border-white/20 text-zinc-400" },
  failed: { label: "Payment Failed", cls: "border-brand-magenta/60 text-brand-magenta" },
  expired: { label: "Expired — Unpaid", cls: "border-brand-magenta/60 text-brand-magenta" },
  manual: { label: "Manual", cls: "border-white/20 text-zinc-400" },
};

const OrderCard = ({ order, adminKey, onStatus, onRefresh }) => {
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [uploadingProof, setUploadingProof] = useState(false);
  const current = STATUS_STAGES.some((s) => s.id === order.status) ? order.status : "received";
  const stage = STATUS_STAGES.find((s) => s.id === current);
  const rushItems = (order.items || []).filter((i) => (i.rush_fee || 0) > 0);
  const rushLabel = rushItems.find((i) => i.rush_label)?.rush_label;
  const payments = order.payments || [];
  const paidAmt = payments.length
    ? payments.filter((p) => p.status === "confirmed").reduce((s, p) => s + (p.amount || 0), 0)
    : order.payment_status === "paid" || order.payment_status === "manual" ? order.total : 0;
  const balanceDue = Math.max(0, Math.round((order.total - paidAmt) * 100) / 100);
  const awaitingCash = payments.find((p) => p.method === "cash" && p.status === "awaiting");
  const payMeta = PAYMENT_META[order.payment_status];

  const uploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingProof(true);
    try {
      await adminUploadProof(order.id, file, adminKey);
      toast.success(`Proof uploaded for ${order.id} — customer notified`);
      onRefresh?.();
    } catch (err) {
      toast.error("Proof upload failed", { description: err?.response?.data?.detail || "Try again" });
    } finally {
      setUploadingProof(false);
      e.target.value = "";
    }
  };

  const changeStatus = async (e, confirmUnpaid = false) => {
    // Capture synchronously: the controlled <select> reverts its DOM value after a failed
    // update, so e.target.value must not be re-read after an await.
    const next = typeof e === "string" ? e : e.target.value;
    setSaving(true);
    try {
      await adminSetOrderStatus(order.id, next, adminKey, confirmUnpaid);
      onStatus(order.id, next);
      toast.success(`${order.id} → ${STATUS_STAGES.find((s) => s.id === next).label}`);
    } catch (err) {
      if (err?.response?.status === 409) {
        // Release guard: outstanding balance — require explicit acknowledgment
        if (window.confirm(err.response.data.detail)) {
          await changeStatus(next, true);
          return;
        }
      } else {
        toast.error("Status update failed", { description: err?.response?.data?.detail || "Try again" });
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmCash = async (amount) => {
    const msg = amount
      ? `Confirm you have received ${money(amount)} in CASH for order ${order.id}?`
      : `Record a cash payment of ${money(balanceDue)} (full outstanding balance) for order ${order.id}?`;
    if (!window.confirm(msg)) return;
    setPayBusy(true);
    try {
      await adminConfirmCash(order.id, adminKey, amount);
      toast.success(`Cash payment confirmed for ${order.id}`);
      onRefresh?.();
    } catch (err) {
      toast.error("Cash confirmation failed", { description: err?.response?.data?.detail || "Try again" });
    } finally {
      setPayBusy(false);
    }
  };

  const deleteOrder = async () => {
    if (!window.confirm(`Delete order ${order.id} permanently? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await adminDeleteOrder(order.id, adminKey);
      toast.success(`Order ${order.id} deleted`);
      onRefresh?.();
    } catch (err) {
      toast.error("Delete failed", { description: err?.response?.data?.detail || "Try again" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid={`admin-order-${order.id}`} className="border border-white/10 bg-ink-3 p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2 font-mono text-sm font-bold text-brand-yellow">
          {order.id}
          {order.payment_status && payMeta && (
            <span
              data-testid={`admin-order-payment-${order.id}`}
              className={`border px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest ${payMeta.cls}`}
            >
              {payMeta.label}
            </span>
          )}
          {rushItems.length > 0 && (
            <span
              data-testid={`admin-order-rush-${order.id}`}
              className="border border-brand-yellow/60 bg-brand-yellow/10 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-brand-yellow"
            >
              ⚡ Rush Order
            </span>
          )}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          {fmtDate(order.created_at)}
        </span>
      </div>
      {rushLabel && (
        <p
          data-testid={`admin-order-rush-timeline-${order.id}`}
          className="mt-2 font-mono text-[10px] font-bold uppercase tracking-wider text-brand-yellow"
        >
          {rushLabel}
        </p>
      )}
      {payments.length > 0 && (
        <div
          data-testid={`admin-order-payment-detail-${order.id}`}
          className="mt-3 border border-white/10 bg-ink px-4 py-3"
        >
          <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            <span>
              {order.payment_method === "cash" ? "Cash" : "WiPay"}
              {order.payment_plan === "deposit" ? " • 50% Deposit Plan" : " • Full Payment"}
            </span>
            <span className="text-zinc-600">|</span>
            <span>Paid <b className="text-emerald-400">{money(paidAmt)}</b></span>
            <span>Balance <b className={balanceDue > 0.009 ? "text-brand-magenta" : "text-emerald-400"}>{money(balanceDue)}</b></span>
          </div>
          {order.payment_plan === "deposit" && (
            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-[10px] text-zinc-500 sm:grid-cols-4">
              <span>Order Total <b className="text-white">{money(order.total)}</b></span>
              <span>Deposit Required <b className="text-brand-yellow">{money(order.deposit_amount)}</b></span>
              <span>Deposit Paid <b className="text-emerald-400">{money(paidAmt)}</b></span>
              <span>Balance Outstanding <b className={balanceDue > 0.009 ? "text-brand-magenta" : "text-emerald-400"}>{money(balanceDue)}</b></span>
            </div>
          )}
          <ul className="mt-2 space-y-0.5 border-t border-white/10 pt-2">
            {payments.map((p) => (
              <li key={p.id} className="font-mono text-[9px] uppercase tracking-wider text-zinc-600">
                {money(p.amount)} • {p.method === "wipay" ? "WiPay" : "Cash"} • {p.kind} • {p.status}
                {p.confirmed_at && ` • ${new Date(p.confirmed_at).toLocaleDateString("en-JM", { dateStyle: "medium" })}`}
              </li>
            ))}
          </ul>
          {order.payment_status !== "expired" && (awaitingCash || balanceDue > 0.009) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {awaitingCash && (
                <button
                  type="button"
                  data-testid={`admin-order-confirm-cash-${order.id}`}
                  onClick={() => confirmCash(awaitingCash.amount)}
                  disabled={payBusy}
                  className="border border-brand-yellow/60 px-3 py-1.5 font-display text-xs font-bold uppercase tracking-widest text-brand-yellow transition-colors hover:bg-brand-yellow hover:text-ink disabled:opacity-50"
                >
                  {payBusy ? "Saving…" : `Confirm Cash Received — ${money(awaitingCash.amount)}`}
                </button>
              )}
              {!awaitingCash && balanceDue > 0.009 && (
                <button
                  type="button"
                  data-testid={`admin-order-record-cash-${order.id}`}
                  onClick={() => confirmCash(null)}
                  disabled={payBusy}
                  className="border border-brand-yellow/60 px-3 py-1.5 font-display text-xs font-bold uppercase tracking-widest text-brand-yellow transition-colors hover:bg-brand-yellow hover:text-ink disabled:opacity-50"
                >
                  {payBusy ? "Saving…" : `Record Cash Balance — ${money(balanceDue)}`}
                </button>
              )}
            </div>
          )}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <span className={`border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${stage.cls}`}>
          {stage.label}
        </span>
        <select
          data-testid={`admin-order-status-${order.id}`}
          value={current}
          disabled={saving}
          onChange={changeStatus}
          className="border border-white/15 bg-ink px-3 py-1.5 font-mono text-xs text-zinc-200 outline-none transition-colors focus:border-brand-yellow disabled:opacity-50"
        >
          {STATUS_STAGES.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-white/10 pt-3">
        <label
          data-testid={`admin-order-proof-upload-${order.id}`}
          className={`flex cursor-pointer items-center gap-2 border border-brand-cyan/50 px-3 py-1.5 font-display text-xs font-bold uppercase tracking-widest text-brand-cyan transition-colors hover:bg-brand-cyan hover:text-ink ${uploadingProof ? "pointer-events-none opacity-50" : ""}`}
        >
          <Upload className="h-3.5 w-3.5" />
          {uploadingProof ? "Uploading…" : order.proof ? "Replace Proof" : "Upload Proof"}
          <input
            type="file"
            accept=".png,.svg,.ai,.pdf,.jpg,.jpeg"
            className="hidden"
            disabled={uploadingProof}
            onChange={uploadProof}
            data-testid={`admin-order-proof-input-${order.id}`}
          />
        </label>
        {order.proof_status && (
          <span
            data-testid={`admin-order-proof-status-${order.id}`}
            className={`border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${
              order.proof_status === "approved"
                ? "border-emerald-400/60 text-emerald-400"
                : order.proof_status === "changes_requested"
                  ? "border-brand-magenta/60 text-brand-magenta"
                  : "border-brand-yellow/60 text-brand-yellow"
            }`}
          >
            {order.proof_status === "awaiting_approval"
              ? "Proof Awaiting Approval"
              : order.proof_status === "approved"
                ? "Proof Approved"
                : "Proof Changes Requested"}
          </span>
        )}
        {order.proof && (
          <a
            data-testid={`admin-order-proof-view-${order.id}`}
            href={`${API}/files/${order.proof.storage_path}`}
            target="_blank"
            rel="noreferrer"
            className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 underline underline-offset-2 hover:text-brand-cyan"
          >
            View Proof
          </a>
        )}
        {order.proof_note && (
          <span className="font-mono text-[10px] text-zinc-500">Note: {order.proof_note}</span>
        )}
        <button
          type="button"
          data-testid={`admin-order-delete-${order.id}`}
          onClick={deleteOrder}
          disabled={deleting}
          className="ml-auto flex items-center gap-2 border border-brand-magenta/50 px-3 py-1.5 font-display text-xs font-bold uppercase tracking-widest text-brand-magenta transition-colors hover:bg-brand-magenta hover:text-ink disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
          {deleting ? "Deleting…" : "Delete"}
        </button>
      </div>
      <div className="mt-3 grid gap-1 text-sm">
        <p className="text-white">
          {order.customer?.name} <span className="text-zinc-500">• {order.customer?.email}</span>
          {order.customer?.phone && <span className="text-zinc-500"> • {order.customer.phone}</span>}
        </p>
        <p className="font-mono text-xs text-zinc-400">
          {order.shipping?.address}, {order.shipping?.city} {order.shipping?.zip} — {order.shipping?.method}
        </p>
      </div>
      <ul className="mt-3 space-y-1 border-t border-white/10 pt-3">
        {(order.items || []).map((i, idx) => (
          <li key={idx} className="flex justify-between gap-3 font-mono text-xs text-zinc-300">
            <span>
              {i.qty}× {i.name}
              {i.config && <span className="text-brand-cyan"> ({i.config})</span>}
              {(i.rush_fee || 0) > 0 && (
                <span className="font-bold text-brand-yellow"> [⚡ RUSH +{money(i.rush_fee)}]</span>
              )}
              {i.artwork && <span className="text-zinc-500"> [art: {i.artwork}]</span>}
            </span>
            <span>{money(i.unit_price * i.qty + (i.rush_fee || 0))}</span>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex justify-between border-t border-white/10 pt-3 font-mono text-sm">
        <span className="uppercase tracking-widest text-zinc-500">Total</span>
        <span className="font-bold text-white">{money(order.total)}</span>
      </div>
    </div>
  );
};

const InquiryCard = ({ inq }) => (
  <div data-testid={`admin-inquiry-${inq.id}`} className="border border-white/10 bg-ink-3 p-5">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="font-display text-base font-bold uppercase text-white">
        {inq.name} {inq.company && <span className="text-brand-cyan">• {inq.company}</span>}
      </span>
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
        {fmtDate(inq.created_at)}
      </span>
    </div>
    <p className="mt-2 font-mono text-xs text-zinc-400">
      {inq.email} {inq.category && `• ${inq.category}`} {inq.quantity && `• qty ${inq.quantity}`}
      {inq.deadline && `• due ${inq.deadline}`}
    </p>
    {inq.message && <p className="mt-2 text-sm leading-relaxed text-zinc-300">{inq.message}</p>}
  </div>
);

const FileCard = ({ file }) => (
  <div
    data-testid={`admin-file-${file.id}`}
    className="flex items-center justify-between gap-3 border border-white/10 bg-ink-3 p-4"
  >
    <div>
      <p className="font-mono text-sm text-white">{file.original_filename}</p>
      <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
        {(file.size / 1024).toFixed(0)} KB • {fmtDate(file.created_at)}
      </p>
    </div>
    <a
      data-testid={`admin-file-download-${file.id}`}
      href={`${API}/files/${file.storage_path}`}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-1.5 border border-white/15 px-3 py-1.5 font-display text-xs font-bold uppercase tracking-widest text-zinc-300 transition-colors hover:border-brand-cyan hover:text-brand-cyan"
    >
      <Download className="h-3.5 w-3.5" /> View
    </a>
  </div>
);
const ProductImagesPanel = ({ adminKey }) => {
  const [products, setProducts] = useState([]);
  const [loadErr, setLoadErr] = useState("");
  const [busy, setBusy] = useState("");
  const { images, refresh } = useProductImages();

  useEffect(() => {
    fetchProducts()
      .then((data) => setProducts(data || []))
      .catch(() => setLoadErr("Could not load products"));
  }, []);

  const upload = async (slug, file) => {
    if (!file) return;
    setBusy(slug);
    try {
      await adminUploadProductImage(slug, file, adminKey);
      await refresh();
      toast.success("Photo updated", { description: "The storefront now shows your new image." });
    } catch (err) {
      toast.error("Upload failed", { description: err?.response?.data?.detail || "Try again" });
    } finally {
      setBusy("");
    }
  };

  const revert = async (slug) => {
    if (!window.confirm("Remove your custom photo and restore the default image for this product?")) return;
    setBusy(slug);
    try {
      await adminDeleteProductImage(slug, adminKey);
      await refresh();
      toast.success("Default image restored");
    } catch (err) {
      toast.error("Revert failed", { description: err?.response?.data?.detail || "Try again" });
    } finally {
      setBusy("");
    }
  };

  if (loadErr) {
    return <p data-testid="admin-images-error" className="font-mono text-xs uppercase tracking-wider text-brand-magenta">{loadErr}</p>;
  }

  return (
    <div data-testid="admin-images-panel">
      <p className="mb-4 font-mono text-xs uppercase tracking-widest text-zinc-500">
        Upload your own photo for any product — it replaces the placeholder on the storefront instantly. PNG, JPG or WEBP, max 15MB.
      </p>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {products.map((p) => {
          const isCustom = Boolean(images[p.slug]);
          const img = productImageUrl(images, p.slug, PRODUCT_IMAGES[p.slug] || CATEGORY_IMAGES[p.category_slug]);
          return (
            <div key={p.slug} data-testid={`admin-image-card-${p.slug}`} className="border border-white/10 bg-ink-2">
              <div className="relative aspect-[4/3] overflow-hidden bg-ink-3">
                {img && <img src={img} alt={p.name} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />}
                {isCustom && (
                  <span data-testid={`admin-image-custom-badge-${p.slug}`} className="absolute left-2 top-2 bg-brand-cyan px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-ink">
                    Your Photo
                  </span>
                )}
              </div>
              <div className="p-3">
                <p className="truncate font-display text-xs font-bold uppercase text-white">{p.name}</p>
                <div className="mt-2 flex gap-2">
                  <label
                    data-testid={`admin-image-upload-${p.slug}`}
                    className={`flex-1 cursor-pointer border border-brand-yellow/60 px-2 py-1.5 text-center font-display text-[10px] font-bold uppercase tracking-widest text-brand-yellow transition-colors hover:bg-brand-yellow hover:text-ink ${busy === p.slug ? "pointer-events-none opacity-50" : ""}`}
                  >
                    {busy === p.slug ? "Working…" : isCustom ? "Replace" : "Upload"}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        upload(p.slug, e.target.files?.[0]);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {isCustom && (
                    <button
                      type="button"
                      data-testid={`admin-image-revert-${p.slug}`}
                      onClick={() => revert(p.slug)}
                      disabled={busy === p.slug}
                      className="border border-brand-magenta/50 px-2 py-1.5 font-display text-[10px] font-bold uppercase tracking-widest text-brand-magenta transition-colors hover:bg-brand-magenta hover:text-ink disabled:opacity-50"
                    >
                      Revert
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};



const Admin = () => {
  const [key, setKey] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [draft, setDraft] = useState("");
  const [section, setSection] = useState("orders");
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (credential, kind) => {
    if (kind === "settings" || kind === "images" || kind === "tracking") {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await adminFetch(kind, credential);
      setItems(data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setKey("");
        setError("Invalid admin key — try again");
      } else {
        setError("Could not load data. Try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key, section);
  }, [key, section, load]);

  const saveKey = (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    sessionStorage.setItem(STORAGE_KEY, draft.trim());
    setKey(draft.trim());
    setDraft("");
  };

  const signOut = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setKey("");
    setItems([]);
  };

  if (!key) {
    return (
      <div data-testid="admin-gate" className="mx-auto max-w-md px-4 pb-32 pt-48 sm:px-6">
        <div className="border border-white/10 bg-ink-2">
          <div className="h-1 cmyk-line" />
          <form onSubmit={saveKey} className="p-8">
            <Lock className="h-8 w-8 text-brand-yellow" />
            <h1 className="mt-4 font-display text-3xl font-black uppercase tracking-tight text-white">
              Studio Admin
            </h1>
            <p className="mt-1 font-mono text-xs uppercase tracking-widest text-zinc-500">
              Enter the admin key to continue
            </p>
            <input
              data-testid="admin-key-input"
              type="password"
              autoComplete="off"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Admin key"
              className="mt-6 w-full border border-white/15 bg-ink px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none focus:border-brand-yellow"
            />
            {error && (
              <p data-testid="admin-gate-error" className="mt-2 font-mono text-[10px] uppercase tracking-wider text-brand-magenta">
                {error}
              </p>
            )}
            <button
              data-testid="admin-key-submit"
              type="submit"
              className="mt-4 w-full bg-brand-yellow py-3.5 font-display text-base font-black uppercase tracking-widest text-ink transition-colors hover:bg-brand-cyan"
            >
              Unlock Dashboard
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="mx-auto max-w-5xl px-4 pb-24 pt-40 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.4em] text-brand-cyan">Studio Admin</p>
          <h1 className="mt-1 font-display text-4xl font-black uppercase tracking-tight text-white">
            Production Board
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-refresh"
            onClick={() => load(key, section)}
            className="flex items-center gap-2 border border-white/15 px-4 py-2 font-display text-xs font-bold uppercase tracking-widest text-zinc-300 hover:border-brand-cyan hover:text-brand-cyan"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button
            data-testid="admin-sign-out"
            onClick={signOut}
            className="flex items-center gap-2 border border-white/15 px-4 py-2 font-display text-xs font-bold uppercase tracking-widest text-zinc-300 hover:border-brand-magenta hover:text-brand-magenta"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign Out
          </button>
        </div>
      </div>

      <div className="mt-8 flex gap-2 border-b border-white/10 pb-px">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            data-testid={`admin-tab-${s.id}`}
            onClick={() => setSection(s.id)}
            className={`flex items-center gap-2 border-b-2 px-4 py-3 font-display text-sm font-bold uppercase tracking-widest transition-colors ${
              section === s.id
                ? "border-brand-yellow text-brand-yellow"
                : "border-transparent text-zinc-500 hover:text-white"
            }`}
          >
            <s.icon className="h-4 w-4" /> {s.label}
          </button>
        ))}
      </div>

      {error && (
        <p data-testid="admin-error" className="mt-4 font-mono text-xs uppercase tracking-wider text-brand-magenta">
          {error}
        </p>
      )}

      <div className="mt-6 space-y-4">
        {section === "images" ? (
          <ProductImagesPanel adminKey={key} />
        ) : section === "tracking" ? (
          <AdminTracking adminKey={key} />
        ) : loading ? (
          <div className="space-y-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-28 animate-pulse border border-white/10 bg-ink-2" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="halftone border border-dashed border-white/15 p-14 text-center">
            <p className="font-display text-2xl font-bold uppercase text-zinc-500">Nothing here yet</p>
            <p className="mt-1 font-mono text-xs uppercase tracking-widest text-zinc-600">
              New {section} will appear as they come in
            </p>
          </div>
        ) : section === "orders" ? (
          items.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              adminKey={key}
              onStatus={(id, st) =>
                setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status: st } : x)))
              }
              onRefresh={() => load(key, "orders")}
            />
          ))
        ) : section === "inquiries" ? (
          items.map((i) => <InquiryCard key={i.id} inq={i} />)
        ) : section === "settings" ? (
          <AdminWiPay adminKey={key} />
        ) : (
          items.map((f) => <FileCard key={f.id} file={f} />)
        )}
      </div>
    </div>
  );
};

export default Admin;
